import LicenseKeyModel from '@shared/models/organizations/LicenseKeyModel';
import OrganizationModel from '@shared/models/organizations/OrganizationModel';
import UnexpectedResult from '@shared/models/result/UnexpectedResult';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlertsStore } from './state/AlertsContainer';
import { useOrganizationStore } from './state/OrganizationContainer';

export default function useLicenseKeyAssignEditor(licenseKey?: LicenseKeyModel) {
    const { t } = useTranslation();
    const loadingData = useOrganizationStore((state) => state.loadingData);
    const [licenseKeyAssign, setLicenseKeyAssign] = useState<LicenseKeyModel | undefined>(licenseKey);

    const [assignKey, setAssignKey] = useState<NewOrganizationWithKeyRequest>({
        name: '',
        description: '',
        key: licenseKey?.accessCode ?? '',
        customBillingUrl: '',
    });

    const resetAssignKey = () => {
        setAssignKey({
            name: '',
            description: '',
            key: licenseKey?.accessCode ?? '',
            customBillingUrl: '',
        });
    };

    const handleInputChange = (field: string, value: string) => {
        setAssignKey((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!assignKey.name || !assignKey.description) {
            useAlertsStore.getState().showError(t('common:requiredFields'));
            return new UnexpectedResult<OrganizationModel>();
        }
        if (!licenseKeyAssign?.originatedByOrganizationId || !assignKey) {
            useAlertsStore.getState().showError(t('common:saveError'));
            return new UnexpectedResult<OrganizationModel>();
        }
        try {
            useOrganizationStore.getState().handleLoadingDataChange('managedKeys', true);

            const result = await useOrganizationStore
                .getState()
                .createManagedOrg(licenseKeyAssign?.originatedByOrganizationId, assignKey);
            if (result.resultType === 'Ok' && result.data) {
                useAlertsStore.getState().showSuccess(t('common:saved'));
            } else {
                useAlertsStore.getState().showError(t('common:saveError'));
            }
            return result;
        } catch (error) {
            useAlertsStore.getState().showError(t('common:saveError'));
            return new UnexpectedResult<OrganizationModel>();
        } finally {
            useOrganizationStore.getState().handleLoadingDataChange('managedKeys', false);
        }
    };

    return {
        assignKey,
        licenseKeyAssign,
        setLicenseKeyAssign,
        resetAssignKey,
        handleInputChange,
        handleSave,
        isLoading: loadingData.managedOrgs,
    };
}
